import './paymentEditor.less';

import * as _ from 'lodash';
import { MeetingAccountSource } from '../../objects/Meeting';
import { calculateAmount } from '../calculation/PaymentsCalculation';
import { wageModelGroups } from '../../objects/WageType';

/**
 * @typedef { import('./PaymentEditorModel').PaymentEditorModel } PaymentEditorModel
 * @typedef { import('../../objects/Payment').Payment } Payment
 * @typedef { import('../../objects/Attendance').Attendance } Attendance
 * @typedef { import('../../objects/WageType').WageType } WageType
*/

class PaymentEditorController {

    constructor() {
        this.missingPeriod = false;

        /** @type {PaymentEditorModel} */
        this.model = null;
        /** @type {Payment} */
        this.payment = null;
        /** @type {Attendance} */
        this.attendance = null;
        /** @type {WageType[]} */
        this.activeWageTypes = null;
        /** @type {WageType[]} */
        this.activeWageTypes = null;
    }

    /**
     * 
     * @param {any} changed
     */
    $onChanges(changed) {
        if (changed.model) {
            if (this.model) {
                this.payment = this.model.payment;
                this.attendance = this.model.payslipModel.attendance;
                this.wasLocked = this.model.original?.payment.IsLocked;
                this.updateWageTypes();
                this.loadPeriod();
            } else {
                this.payment = null;
                this.attendance = null;
                this.operation = null;
                this.activeWageTypes = null;
                this.wasLocked = false;
            }
        }

        if (changed.wageTypes) {
            this.updateWageTypes();
        }
    }

    updateWageTypes() {
        if (this.model.enableLostIncome) {
            this.activeWageTypes = this.wageTypes;
        } else {
            this.activeWageTypes = _.filter(this.wageTypes, x => x.WageModelGroup !== wageModelGroups.lostIncome);
        }
    }

    isEmployerEnabled() {
        if (!this.period) return false;

        if (this.period.WageModel !== 4)
            return false;

        return !this.model.isPersisted;
    }

    isUnitPriceRequired() {
        return this.payment?.WageType?.RequireUnitPrice;
    }

    isUnitPriceEnabled() {
        if (!this.period) return false;

        const wm = this.period.WageModel;
        if (wm === 1 || wm === 4 || wm === 5) {
            return false;
        }

        return this.period.UnitPrice == null;
    }

    isNumberRequired() {
        return this.payment?.WageType?.RequireNumber || (this.isAmountRequired() && !this.isAmountEnabled());
    }

    isNumberEnabled() {
        if (!this.period) return false;

        if (this.isUnitPriceEnabled()) {
            return _.isFinite(this.payment.UnitPrice);
        }

        return true;
    }

    isAmountRequired() {
        return this.payment?.WageType?.RequireAmount;
    }

    isAmountEnabled() {
        if (!this.period) return false;

        return !this.isNumberEnabled();
    }

    calculate() {
        if (!this.period) return null;

        if (this.period.WageModel === 1) {
            if (_.isFinite(this.payment.Number)) {
                const minutes = Math.round(this.payment.Number * this.period.UnitPeriod);
                return this.period.VariablePrice.findAmount(minutes);
            }
        } else {
            if (_.isFinite(this.payment.UnitPrice) && _.isFinite(this.payment.Number)) {
                return calculateAmount(this.payment.UnitPrice, this.payment.Number);
            }
        }

        return null;
    }

    numberChanged() {
        if (this.isNumberEnabled()) {
            this.payment.Amount = this.calculate();
        }
    }

    unitPriceChanged() {
        if (this.isUnitPriceEnabled()) {
            if (_.isFinite(this.payment.UnitPrice)) {
                this.payment.Amount = this.calculate();
            } else {
                this.payment.Number = null;
                this.payment.Amount = null;
            }
        }
    }

    wageTypeChanged() {
        this.loadPeriod();
        if (this.period != null) {
            if (this.period.UseMeetingAccountSettings) {
                if (this.attendance.Meeting.AccountSource === MeetingAccountSource.meetingOrganization) {
                    this.payment.Account = this.attendance.Meeting.Organization.Account;
                } else if (this.attendance.Meeting.AccountSource === MeetingAccountSource.meetingAccount) {
                    this.payment.Account = this.attendance.Meeting.Account;
                } else {
                    if (this.attendance.isLegacy()) {
                        this.payment.Account = "";
                    } else {
                        this.payment.Account = this.attendance.Mission.MandatePeriod.Organization.Account;
                    }
                }
            } else {
                this.payment.Account = this.period.Account;
            }

            const wm = this.period.WageModel;
            if (wm === 1 || wm === 5) {
                this.payment.Number = null;
                this.payment.Amount = null;
                this.payment.UnitPrice = null;
            } else if (wm == 4) {
                const employers = this.model?.employerOptions;
                if (employers && employers.length > 0) {
                    this.model.selectedEmployer = employers[0].name;
                    this.payment.UnitPrice = employers[0].earning.UnitPrice;
                } else {
                    this.model.selectedEmployer = null;
                    this.payment.UnitPrice = null;
                }
            } else {
                this.payment.Number = null;
                this.payment.Amount = null;
                if (_.isFinite(this.period.UnitPrice)) {
                    this.payment.UnitPrice = this.period.UnitPrice;
                } else {
                    this.payment.UnitPrice = null;
                }
            }
        }
    }

    employerChanged() {
        if (this.payment == null) return;

        const opt = _.find(this.model?.employerOptions, x => x.name === this.model.selectedEmployer);
        if (opt) {
            this.payment.UnitPrice = opt.earning.UnitPrice;
            this.payment.Amount = this.calculate();
        } else {
            this.payment.UnitPrice = null;
            this.payment.Amount = this.calculate();
        }
    }

    loadPeriod() {
        const wt = this.payment.WageType;
        if (wt) {
            this.period = wt.getPeriod(this.attendance.Meeting.Date);
            this.missingPeriod = (this.period == null);
        } else {
            this.period = null;
            this.missingPeriod = false;
        }
    }

    apply() {
        this.model.apply();
        this.onClose();
    }

    remove() {
        this.model.remove();
        this.onClose();
    }

    abort() {
        this.onClose();
    }
}

angular.module("tromanWeb").component('ergoPaymentEditor', {
    template: require('./PaymentEditor.html'),
    bindings: {
        model: '<',
        wageTypes: '<',
        onClose: '&',
    },
    controller: PaymentEditorController
});